import { lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Layout from "@/components/Layout";
import ScrollToHash from "@/components/ScrollToHash";
// Landing page ships in the initial bundle (it is the LCP route); everything else
// is loaded on demand so the first paint stays small.
import HomePage from "@/pages/HomePage";

const SellPage = lazy(() => import("@/pages/SellPage"));
const WhatToSellPage = lazy(() => import("@/pages/WhatToSellPage"));
const ListingProductsPage = lazy(() => import("@/pages/ListingProductsPage"));
const ProductCategoriesPage = lazy(() => import("@/pages/ProductCategoriesPage"));
const ProductDetailsPage = lazy(() => import("@/pages/ProductDetailsPage"));
const PricingProductsPage = lazy(() => import("@/pages/PricingProductsPage"));
const FulfillmentPage = lazy(() => import("@/pages/FulfillmentPage"));
const FBRPage = lazy(() => import("@/pages/FBRPage"));
const GrowPage = lazy(() => import("@/pages/GrowPage"));
const AdvertisingPage = lazy(() => import("@/pages/AdvertisingPage"));
const AdvertisingStartPage = lazy(() => import("@/pages/AdvertisingStartPage"));
const BrandPromotionPage = lazy(() => import("@/pages/BrandPromotionPage"));
const BrandRegistryPage = lazy(() => import("@/pages/BrandRegistryPage"));
const APlusContentPage = lazy(() => import("@/pages/APlusContentPage"));
const NewSellerIncentivesPage = lazy(() => import("@/pages/NewSellerIncentivesPage"));
const StorefrontPage = lazy(() => import("@/pages/StorefrontPage"));
const SupplyChainPage = lazy(() => import("@/pages/SupplyChainPage"));
const MultiChannelPage = lazy(() => import("@/pages/MultiChannelPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const RetailPage = lazy(() => import("@/pages/RetailPage"));
const OptionalCostsPage = lazy(() => import("@/pages/OptionalCostsPage"));
const LearnPage = lazy(() => import("@/pages/LearnPage"));
const WebinarsPage = lazy(() => import("@/pages/WebinarsPage"));
const SellerStoriesPage = lazy(() => import("@/pages/SellerStoriesPage"));
const SellerEventPage = lazy(() => import("@/pages/SellerEventPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const SellerStoryDetailPage = lazy(() => import("@/pages/SellerStoryDetailPage"));
const GetAppPage = lazy(() => import("@/pages/GetAppPage"));
const SellerAppPage = lazy(() => import("@/pages/SellerAppPage"));
const SellerRegisterPage = lazy(() => import("@/pages/SellerRegisterPage"));
const SellerRegisterSuccessPage = lazy(() => import("@/pages/SellerRegisterSuccessPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const SellerEmailPreviewPage = lazy(() => import("@/pages/SellerEmailPreviewPage"));
const LoginChoicePage = lazy(() => import("@/pages/LoginChoicePage"));
const JoinAsPartnerPage = lazy(() => import("@/pages/JoinAsPartnerPage"));
const PartnerStatusPage = lazy(() => import("@/pages/PartnerStatusPage"));
const LeadershipPage = lazy(() => import("@/pages/LeadershipPage"));
const LeaderProfilePage = lazy(() => import("@/pages/LeaderProfilePage"));
const TrustPage = lazy(() => import("@/pages/TrustPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminPartnersPage = lazy(() => import("@/pages/admin/AdminPartnersPage"));
const AdminSellersPage = lazy(() => import("@/pages/admin/AdminSellersPage"));
const AdminAuditPage = lazy(() => import("@/pages/admin/AdminAuditPage"));
const AdminTeamPage = lazy(() => import("@/pages/admin/AdminTeamPage"));
const AdminCommissionPage = lazy(() => import("@/pages/admin/AdminCommissionPage"));
const AdminDownloadsPage = lazy(() => import("@/pages/admin/AdminDownloadsPage"));
const AcceptInvitePage = lazy(() => import("@/pages/admin/AcceptInvitePage"));
const AdminChangePasswordPage = lazy(() => import("@/pages/admin/AdminChangePasswordPage"));
const AdminForgotPasswordPage = lazy(() => import("@/pages/admin/AdminForgotPasswordPage"));
const AdminResetPasswordPage = lazy(() => import("@/pages/admin/AdminResetPasswordPage"));
const AdminEconomicsPage = lazy(() => import("@/pages/admin/AdminEconomicsPage"));
const OAuthConsentPage = lazy(() => import("@/pages/OAuthConsentPage"));
const AdminRoute = lazy(() => import("@/components/AdminRoute"));
// Auth (and with it the backend client) is only needed on admin/registration routes.
const AuthProvider = lazy(() =>
  import("@/hooks/useAuth").then((m) => ({ default: m.AuthProvider }))
);

const queryClient = new QueryClient();

/** Wraps routes that need an authenticated session. */
const Authed = ({ children }: { children: ReactNode }) => (
  <AuthProvider>{children}</AuthProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToHash />
        <Suspense fallback={null}>
        <Routes>
          {/* Default redirect to Arabic */}
          <Route path="/" element={<Navigate to="/ar" replace />} />

          {/* Admin routes (no language prefix, no marketing layout) */}
          <Route path="/admin/login" element={<Authed><AdminLoginPage /></Authed>} />
          <Route path="/.lovable/oauth/consent" element={<Authed><OAuthConsentPage /></Authed>} />
          <Route path="/admin/forgot-password" element={<Authed><AdminForgotPasswordPage /></Authed>} />
          <Route path="/admin/reset-password" element={<Authed><AdminResetPasswordPage /></Authed>} />
          <Route path="/admin/accept-invite" element={<Authed><AcceptInvitePage /></Authed>} />
          <Route
            path="/admin/change-password"
            element={
              <Authed>
                <AdminRoute allowModerator allowOnboarding>
                  <AdminChangePasswordPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/partners"
            element={
              <Authed>
                <AdminRoute allowModerator>
                  <AdminPartnersPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/sellers"
            element={
              <Authed>
                <AdminRoute onboardingOnly>
                  <AdminSellersPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <Authed>
                <AdminRoute allowModerator>
                  <AdminAuditPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/team"
            element={
              <Authed>
                <AdminRoute>
                  <AdminTeamPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/commission"
            element={
              <Authed>
                <AdminRoute allowModerator>
                  <AdminCommissionPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/downloads"
            element={
              <Authed>
                <AdminRoute allowModerator>
                  <AdminDownloadsPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route
            path="/admin/economics"
            element={
              <Authed>
                <AdminRoute allowModerator allowOnboarding>
                  <AdminEconomicsPage />
                </AdminRoute>
              </Authed>
            }
          />
          <Route path="/admin" element={<Navigate to="/admin/partners" replace />} />


          {/* Language-prefixed routes */}
          <Route path="/:lang" element={<LanguageProvider><Layout /></LanguageProvider>}>
            <Route index element={<HomePage />} />
            {/* Start */}
            <Route path="sell" element={<SellPage />} />
            <Route path="sell/what-to-sell" element={<WhatToSellPage />} />
            <Route path="sell/listing-products" element={<ListingProductsPage />} />
            <Route path="sell/product-categories" element={<ProductCategoriesPage />} />
            <Route path="sell/product-details" element={<ProductDetailsPage />} />
            <Route path="sell/pricing-products" element={<PricingProductsPage />} />
            <Route path="sell/fulfillment" element={<FulfillmentPage />} />
            <Route path="sell/fbr" element={<FBRPage />} />
            {/* Grow */}
            <Route path="grow" element={<GrowPage />} />
            <Route path="grow/advertising" element={<AdvertisingPage />} />
            <Route path="grow/advertising/start" element={<AdvertisingStartPage />} />
            <Route path="grow/advertising/brand" element={<BrandPromotionPage />} />
            <Route path="grow/brand-registry" element={<BrandRegistryPage />} />

            <Route path="grow/a-plus-content" element={<APlusContentPage />} />
            <Route path="grow/new-seller-incentives" element={<NewSellerIncentivesPage />} />
            <Route path="grow/storefront" element={<StorefrontPage />} />
            {/* Services */}
            <Route path="services/supply-chain" element={<SupplyChainPage />} />
            <Route path="services/multi-channel" element={<MultiChannelPage />} />
            {/* Pricing */}
            <Route path="pricing" element={<PricingPage />} />
            <Route path="retail" element={<RetailPage />} />
            <Route path="pricing/optional-costs" element={<OptionalCostsPage />} />
            {/* Resources */}
            <Route path="learn" element={<LearnPage />} />
            <Route path="learn/webinars" element={<WebinarsPage />} />
            <Route path="learn/seller-stories" element={<SellerStoriesPage />} />
            <Route path="learn/seller-event" element={<SellerEventPage />} />
            <Route path="learn/seller-stories/:slug" element={<SellerStoryDetailPage />} />
            <Route path="learn/faq" element={<FAQPage />} />
            <Route path="get-app" element={<GetAppPage />} />
            <Route path="seller-app" element={<SellerAppPage />} />
            <Route path="leadership" element={<LeadershipPage />} />
            <Route path="leadership/:slug" element={<LeaderProfilePage />} />
            <Route path="login" element={<LoginChoicePage />} />
            <Route path="partners-program" element={<JoinAsPartnerPage />} />
            <Route path="partner-status" element={<PartnerStatusPage />} />
            <Route path="trust" element={<TrustPage />} />
          </Route>

          {/* Seller registration wizard (standalone, no marketing layout) */}
          <Route path="/:lang/sell/register" element={<Authed><SellerRegisterPage /></Authed>} />
          <Route path="/:lang/sell/register/success" element={<SellerRegisterSuccessPage />} />

          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/sell/email-preview" element={<SellerEmailPreviewPage />} />
          <Route path="/:lang/sell/email-preview" element={<SellerEmailPreviewPage />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
